import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ReloadOutlined } from '@ant-design/icons';
import { Sparkles } from 'lucide-react';

import { ForbiddenView } from '../../../components/ForbiddenView';
import { PageHeader, TabsTruot } from '../../../components/shared';
import { Button } from '../../../components/ui';
import { useT } from '../../../i18n';
import { maLoi } from '../../../lib/errors';
import { useTenantCuaToi } from '../../auth/hooks/useTenantCuaToi';
import { KHOA_BAN_TOKEN } from '../../goi-nap-token/hooks/useGoiNapToken';
import { KHOA_TOKEN_AI, useHanMucTokenAi } from '../../tenant/hooks/useHanMucTokenAi';
import { TabHanMuc } from '../components/TabHanMuc';
import { TabMucSuDung } from '../components/TabMucSuDung';
import { TabNapToken } from '../components/TabNapToken';
import { TheTomTatToken } from '../components/TheTomTatToken';
import { KHOA_MAN_TOKEN_AI, useLaQuanTriToChuc } from '../hooks/useTokenAi';

const CAC_THE = ['su-dung', 'han-muc', 'nap-token'] as const;
const THAM_SO_THE = 'the';

/**
 * Token AI của tổ chức (cổng tenant, `/token-ai`, ADR-0088, ADR-0091, ADR-0123): phần tóm tắt luôn ở trên, ba thẻ Mức
 * sử dụng, Hạn mức, Nạp token. Chỉ token Fox; chỉ quản trị tổ chức (phân hệ `AI_TOKEN`, `PHAN_HE_CHI_QUAN_TRI`).
 * `tenant_id` lấy từ ngữ cảnh đăng nhập, không bao giờ từ URL. Nhân sự nền tảng xem chéo không có tenant nên không vào được.
 */
export function TokenAiView() {
  const { t } = useT();
  const laQuanTri = useLaQuanTriToChuc();
  const { tenantId, dangTai } = useTenantCuaToi();
  const coTheXem = laQuanTri && Boolean(tenantId);
  const q = useHanMucTokenAi(coTheXem ? tenantId : undefined);
  const qc = useQueryClient();
  const [thamSo, setThamSo] = useSearchParams();

  const biTuChoi = maLoi(q.error) === 'PERMISSION_DENIED' || maLoi(q.error) === 'NOT_FOUND';
  if (!dangTai && (!coTheXem || biTuChoi)) return <ForbiddenView />;

  const tieuDe = t('nav.muc.tokenAi');
  const the = thamSo.get(THAM_SO_THE);
  const moSan = CAC_THE.find((k) => k === the) ?? CAC_THE[0];

  const lamMoi = () => {
    void q.refetch();
    void qc.invalidateQueries({ queryKey: KHOA_MAN_TOKEN_AI.tatCa });
    void qc.invalidateQueries({ queryKey: KHOA_TOKEN_AI.tatCa });
    void qc.invalidateQueries({ queryKey: KHOA_BAN_TOKEN.tatCa });
  };

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[{ label: t('nav.nhom.tongQuan') }, { label: tieuDe }]}
          titleIcon={<Sparkles size={18} className="text-primary-600" />}
          title={tieuDe}
          actions={
            <Button variant="ghost" size="md" title={t('chung.lamMoi')} aria-label={t('chung.lamMoi')} onClick={lamMoi}>
              <ReloadOutlined className={q.isFetching ? 'animate-spin' : ''} />
            </Button>
          }
        />

        <TheTomTatToken q={q} />

        {tenantId && (
          <TabsTruot
            xuongDong
            defaultActiveKey={moSan}
            onChange={(key) =>
              setThamSo(
                (cu) => {
                  const moi = new URLSearchParams(cu);
                  moi.set(THAM_SO_THE, key);
                  return moi;
                },
                { replace: true },
              )
            }
            items={[
              { key: 'su-dung', label: t('manTokenAi.theSuDung'), children: <TabMucSuDung tenantId={tenantId} /> },
              { key: 'han-muc', label: t('manTokenAi.theHanMuc'), children: <TabHanMuc tenantId={tenantId} /> },
              { key: 'nap-token', label: t('manTokenAi.theNapToken'), children: <TabNapToken tenantId={tenantId} quota={q.data} /> },
            ]}
          />
        )}
      </div>
    </div>
  );
}
